/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import * as React from 'react';

import {
  Button,
  Chip,
  Grid,
  Hidden,
  Skeleton,
  TabContext,
  Typography2,
  breakpoints,
  useMediaQuery,
} from '@coursera/cds-core';
import type { ButtonProps, Button as ButtonType } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import ModuleLayout, {
  MODULE_LAYOUTS,
  getModuleLayoutConfig,
  isHorizontalLayout,
} from 'bundles/cds-labs/components/Modules/ModuleLayout';
import type { ModuleLayoutConfig } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import Eyebrow from 'bundles/cds-labs/components/Modules/components/Eyebrow';
import type { ImageType } from 'bundles/cds-labs/components/Modules/components/Image';
import Image from 'bundles/cds-labs/components/Modules/components/Image';
import { isInvertedColor } from 'bundles/cds-labs/components/Modules/utils/colors';
import type { GridConfig } from 'bundles/cds-labs/components/ShowMoreGridSection/ShowMoreGridSection';
// External Shared Dependencies
import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
// eslint-disable-next-line import/no-cycle
import CollectionsContainer from 'bundles/collections-common/components/CollectionsContainer';
// Eventing
import withSingleTracked from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/cds-labs';

const TrackedChip = withSingleTracked({ type: 'BUTTON' })(Chip);

export type CTA = {
  text?: string | null;
  target?: string | null;
  variant?: ButtonProps['variant'] | null;
  showCtaArrowIcon?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

export const headerOrder = ['h2', 'h3', 'h4', 'h5', 'h6'] as const;

export type HeaderLevel = (typeof headerOrder)[number];

export type Filter = {
  id: string;
  filterName: string;
  filterImage?: ImageType | null;
};

export type Props = {
  headline?: string | null;
  headlineComponent?: HeaderLevel;
  description?: string | null;
  showAllEntries?: boolean | null;
  filters?: Filter[] | null;
  collections?: CarouselCollection[];
  isCollectionsLoading?: boolean;
  onFilterChange?: (index: number, filterName: string) => void;
  currentCollectionSetIndex?: number;
  numOfEntriesPerCollection?: number;
  cta?: CTA;
  customCtaButton?: React.ReactNode;
  customChipStyles?: SerializedStyles;
  productCardTrackingName?: string | null;
  // show filters even if there is only one filter search experiment
  alwaysShowChipFilters?: boolean;
  eventingData?: Record<string, any>;
  customHeadline?: JSX.Element;
  eyebrow?: {
    image?: ImageType;
  };
} & ModuleLayoutConfig;

const styles = {
  heading: css`
    /* Required for the skeleton */
    margin-bottom: var(--cds-spacing-200) !important;
  `,

  description: css`
    margin-bottom: var(--cds-spacing-200);
  `,

  filters: css`
    margin-bottom: var(--cds-spacing-100);

    && {
      gap: var(--cds-spacing-100);
    }
  `,

  alignCta: css`
    margin-top: var(--cds-spacing-200);
  `,

  ghostAlignment: css`
    margin-left: calc(var(--cds-spacing-200) * -1);
  `,

  collectionsWrapper: css`
    width: 100%;
    align-self: flex-start;
  `,

  collectionWrapperVertical: css`
    margin-top: calc(-1 * var(--cds-spacing-300));

    ${breakpoints.down('xs')} {
      margin-top: 0;
    }
  `,

  collections: css`
    margin-top: calc(-1 * var(--cds-spacing-300));
    margin-bottom: calc(-1 * var(--cds-spacing-300));

    .cds-ProductCard-grid {
      min-width: unset;
    }
  `,

  button: css`
    &:hover,
    &:active,
    &:visited {
      text-decoration: none;
    }
  `,

  chip: css`
    &.cds-Chip-root {
      color: var(--cds-color-neutral-primary);
      background-color: var(--cds-color-white-0);
      border-color: var(--cds-color-neutral-disabled-strong);
    }

    &.cds-Chip-selected {
      color: var(--cds-color-blue-700);
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
      border-color: var(--cds-color-blue-700);
    }

    &.cds-Chip-root:hover {
      color: var(--cds-color-neutral-primary);
      background-color: var(--cds-color-neutral-disabled-weak);
      border-color: var(--cds-color-neutral-primary);
    }
  `,

  imageFilter: css`
    padding: var(--cds-spacing-100);
  `,
  contentContainer: (isTwoByTenLayout: boolean, hasFilters: boolean) => css`
    ${breakpoints.up(isTwoByTenLayout ? 'lg' : 'md')} {
      margin-top: ${hasFilters ? 'var(--cds-spacing-1400)' : 'var(--cds-spacing-600)'};
    }
  `,
};

function CollectionsModule(props: Props) {
  const {
    filters,
    layout,
    showAllEntries,
    onFilterChange,
    backgroundColor,
    cta,
    description,
    headline,
    headlineComponent,
    collections = [],
    isCollectionsLoading,
    currentCollectionSetIndex = 0,
    numOfEntriesPerCollection,
    customCtaButton,
    customChipStyles,
    alwaysShowChipFilters,
    productCardTrackingName,
    eventingData,
    customHeadline,
    eyebrow,
  } = props;
  const layoutConfig = getModuleLayoutConfig(props);
  const isTabletOrSmaller = useMediaQuery(breakpoints.down('sm'));
  const isTwoByTenLayout =
    layout === MODULE_LAYOUTS.text2image10 ||
    layout === MODULE_LAYOUTS.image10text2 ||
    layout === MODULE_LAYOUTS.text10image2 ||
    layout === MODULE_LAYOUTS.image2text10;
  const isSideBySideLayout =
    layout !== MODULE_LAYOUTS.textTopImageBottom &&
    layout !== MODULE_LAYOUTS.imageTopTextBottom &&
    layout !== MODULE_LAYOUTS.noImage;

  // show one less entry if there is an odd number of entries, to prevent an orhpaned card on the last row
  const numOfEntriesPerCollectionToDisplay =
    showAllEntries && numOfEntriesPerCollection && numOfEntriesPerCollection % 2 !== 0 && isTabletOrSmaller
      ? numOfEntriesPerCollection - 1
      : numOfEntriesPerCollection;

  const isHorizontal = isHorizontalLayout({ layout });
  const itemsGridConfig: GridConfig = isHorizontal ? { lg: 4, md: 4, sm: 6, xs: 12 } : { lg: 3, md: 3, sm: 6, xs: 12 };
  const collectionsToDisplay = numOfEntriesPerCollectionToDisplay
    ? collections.map((collection) => ({
        ...collection,
        entities: collection.entities.slice(0, numOfEntriesPerCollectionToDisplay),
      }))
    : collections;

  const isInverted = isInvertedColor(backgroundColor ?? '');
  let variantOfCta: React.ComponentProps<typeof ButtonType>['variant'] = cta?.variant ?? 'secondary';
  if (cta?.variant === 'ghost') {
    variantOfCta = isInverted ? 'ghostInvert' : 'ghost';
  }

  let button: React.ReactNode;

  if (customCtaButton) {
    button = customCtaButton;
  } else {
    button =
      cta?.text && cta?.target ? (
        <div css={[styles.alignCta, cta.variant === 'ghost' ? styles.ghostAlignment : undefined]}>
          <Button
            component="a"
            variant={variantOfCta}
            icon={cta.showCtaArrowIcon || cta.variant === 'ghost' ? <ArrowNextIcon /> : undefined}
            href={cta.target}
            onClick={cta.onClick}
            css={cta.variant === 'ghost' ? null : styles.button}
          >
            {cta.text}
          </Button>
        </div>
      ) : null;
  }

  // only show image filters if all filters have images
  const shouldRenderImagesForFilters =
    filters?.reduce((acc, filter) => {
      return filter.filterImage ? acc + 1 : acc;
    }, 0) === filters?.length;
  const hasFilters = !!filters && (filters.length > 1 || !!alwaysShowChipFilters);

  const renderImage = () => {
    return (
      <div css={[styles.collectionsWrapper, isHorizontal ? undefined : styles.collectionWrapperVertical]}>
        <TabContext value={currentCollectionSetIndex.toString()}>
          {hasFilters && (
            <Grid
              container
              aria-label={_t('Filter By')}
              css={styles.filters}
              role="tablist"
              wrap="wrap"
              direction="row"
            >
              {filters &&
                filters.map(({ filterName, id, filterImage }, idx) => {
                  let tabContents: React.ReactText;
                  if (shouldRenderImagesForFilters) {
                    // @ts-expect-error Tabs only accepts ReactText, this works as a child.
                    tabContents = (
                      <div css={styles.imageFilter}>
                        <Image {...filterImage} description="" height={28} />
                      </div>
                    );
                  } else {
                    tabContents = filterName ?? '';
                  }

                  return (
                    <TrackedChip
                      key={id}
                      selected={currentCollectionSetIndex === idx}
                      aria-selected={currentCollectionSetIndex === idx}
                      aria-checked={undefined}
                      onClick={() => onFilterChange?.(idx, filterName)}
                      css={shouldRenderImagesForFilters ? styles.chip : customChipStyles}
                      aria-label={filterName}
                      role="tab"
                      trackingName="collections_filter_chip"
                    >
                      {tabContents}
                    </TrackedChip>
                  );
                })}
            </Grid>
          )}
        </TabContext>
        <div css={styles.collections} data-e2e={isCollectionsLoading ? 'collections-loading' : 'collections-loaded'}>
          <CollectionsContainer
            eventingData={eventingData}
            productCardTrackingName={productCardTrackingName}
            collections={collectionsToDisplay}
            isCollectionsLoading={isCollectionsLoading}
            itemsGridConfig={itemsGridConfig}
            hideLoadingTitle
            showAllEntries={showAllEntries ?? false}
            renderTitle={() => null}
          />
        </div>
        <Hidden {...(isTwoByTenLayout ? { lgUp: true } : { mdUp: true })}>{button}</Hidden>
      </div>
    );
  };

  const renderContent = () => {
    if (isCollectionsLoading && !headline && !customHeadline) {
      return (
        <div>
          <Skeleton data-testid="skeleton" width="70%" css={styles.heading} height={40} />
        </div>
      );
    }
    return (
      <div css={isSideBySideLayout ? styles.contentContainer(isTwoByTenLayout, hasFilters) : null}>
        {eyebrow?.image && <Eyebrow image={eyebrow.image} />}
        {customHeadline || (
          <Typography2 variant="titleSmall" component={headlineComponent ?? 'h2'} color="inherit" css={styles.heading}>
            {headline}
          </Typography2>
        )}
        {description && (
          <Typography2 variant="bodyPrimary" component="p" color="inherit" css={styles.description}>
            {description}
          </Typography2>
        )}
        <Hidden {...(isTwoByTenLayout ? { mdDown: true } : { smDown: true })}>{button}</Hidden>
      </div>
    );
  };

  return <ModuleLayout renderContent={renderContent} renderImage={renderImage} assembly={layoutConfig} />;
}

export default CollectionsModule;
